import { format, isValid, parseISO } from "date-fns";
import { fullDateFullYearFormat } from "@common/helpers/date";
import { formatExactDateRangePeriod } from "@helpers/formatExactDateRangePeriod";

const ISO_DATE_FORMAT = "yyyy-MM-dd";

/**
 * Formats a date range compactly for close template application display
 * (headers, selects, etc.).
 *
 * Priority:
 *  1. Exact full year   → "2025"
 *  2. Exact quarter     → "Q1 2025"
 *  3. Exact full month  → "January 2025"
 *  4. Same start/end    → "Jan 1, 2025"
 *  5. Any other range   → "Jan 1, 2025 – Dec 31, 2025"
 */
export function formatCloseTemplateDateRange(
    startDate: string | null,
    endDate: string | null,
): string {
    if (!startDate && !endDate) {
        return "-";
    }
    if (!startDate) {
        const end = parseISO(endDate!);
        if (!isValid(end)) {
            return "-";
        }
        return `Until ${format(end, fullDateFullYearFormat)}`;
    }
    if (!endDate) {
        const start = parseISO(startDate);
        if (!isValid(start)) {
            return "-";
        }
        return `From ${format(start, fullDateFullYearFormat)}`;
    }

    const start = parseISO(startDate);
    const end = parseISO(endDate);

    if (!isValid(start) || !isValid(end)) {
        return "-";
    }

    const exactPeriodLabel = formatExactDateRangePeriod(start, end);
    if (exactPeriodLabel) {
        return exactPeriodLabel;
    }

    // Same start and end date → display as a single date
    if (format(start, ISO_DATE_FORMAT) === format(end, ISO_DATE_FORMAT)) {
        return format(start, fullDateFullYearFormat);
    }

    // Fallback: explicit date range
    return `${format(start, fullDateFullYearFormat)} – ${format(end, fullDateFullYearFormat)}`;
}
